import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fmtNum, timeAgo } from "@/api";
import { ArrowUp, ArrowDown, ClockCounterClockwise, Trophy, Target } from "@phosphor-icons/react";
import { toast } from "sonner";

function OutcomePill({ outcome }) {
  const map = {
    win: { color: "#00FF66", label: "TP2 Win" },
    tp1: { color: "#F5C518", label: "TP1 Only" },
    loss: { color: "#FF3B30", label: "SL Loss" },
    timeout: { color: "#A1A1AA", label: "Timeout" },
  };
  const m = map[outcome] || { color: "#A1A1AA", label: outcome || "?" };
  return (
    <span
      className="font-mono text-[10px] uppercase tracking-widest px-2 py-[2px] border"
      style={{ color: m.color, borderColor: `${m.color}55`, background: `${m.color}10` }}
    >
      {m.label}
    </span>
  );
}

function StatusPill({ status, mfeR, maeR }) {
  if (status !== "pending") return null;
  return (
    <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-[2px] border border-[#007AFF]/50 text-[#007AFF] bg-[#007AFF]/10 flex items-center gap-1">
      <ClockCounterClockwise size={10} />
      Live · MFE {mfeR?.toFixed?.(2) ?? "0"}R
    </span>
  );
}

export function SignalHistory({ onSelect }) {
  const [status, setStatus] = useState("all");
  const [dir, setDir] = useState("all");
  const [mode, setMode] = useState("all");
  const [days, setDays] = useState(7);

  const history = useQuery({
    queryKey: ["signals-history", status, dir, mode],
    queryFn: () =>
      api.signalsHistory({
        limit: 100,
        ...(status !== "all" ? { status } : {}),
        ...(dir !== "all" ? { direction: dir } : {}),
        ...(mode !== "all" ? { mode } : {}),
      }),
    refetchInterval: 20_000,
  });

  const stats = useQuery({
    queryKey: ["signals-stats", dir, mode, days],
    queryFn: () => api.signalsStats({ direction: dir, mode, days }),
    refetchInterval: 30_000,
  });

  const qc = useQueryClient();
  const resetStats = useMutation({
    mutationFn: api.engineResetStats,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["signals-history"] });
      qc.invalidateQueries({ queryKey: ["signals-stats"] });
      qc.invalidateQueries({ queryKey: ["trigger-history"] });
      qc.invalidateQueries({ queryKey: ["equity-curve"] });
      toast.success(
        `Cleared ${data?.deleted_resolved ?? 0} resolved trades — scoreboard reset`
      );
    },
    onError: () => toast.error("Clear stats failed"),
  });

  const rows = history.data?.signals || [];
  const s = stats.data;

  return (
    <div data-testid="signal-history-panel" className="border border-white/10 bg-[#0A0A0A] h-full flex flex-col">
      <div className="px-4 py-3 border-b border-white/10 bg-[#0f0f0f] flex items-center gap-3 flex-wrap">
        <Trophy size={16} className="text-[#F5C518]" />
        <h2 className="font-display font-bold text-sm tracking-widest uppercase text-white">Signal Replay & Engine Score</h2>

        <div className="flex border border-white/10 ml-2" data-testid="history-dir">
          {["all", "long", "short"].map((d) => (
            <button
              key={d}
              data-testid={`history-dir-${d}`}
              onClick={() => setDir(d)}
              className={`px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${
                dir === d ? "bg-white text-black" : "text-white/60 hover:bg-white/5"
              }`}
            >
              {d}
            </button>
          ))}
        </div>

        <div className="flex border border-white/10" data-testid="history-mode">
          {["all", "scalp", "swing", "position", "macro"].map((m) => (
            <button
              key={m}
              data-testid={`history-mode-${m}`}
              onClick={() => setMode(m)}
              className={`px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${
                mode === m ? "bg-[#F5C518] text-black" : "text-white/60 hover:bg-white/5"
              }`}
            >
              {m}
            </button>
          ))}
        </div>

        <div className="flex border border-white/10">
          {["all", "resolved", "pending"].map((v) => (
            <button
              key={v}
              data-testid={`history-status-${v}`}
              onClick={() => setStatus(v)}
              className={`px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${
                status === v ? "bg-white text-black" : "text-white/60 hover:bg-white/5"
              }`}
            >
              {v}
            </button>
          ))}
        </div>

        <select
          data-testid="history-days"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="bg-black border border-white/10 rounded-none px-2 py-1 text-white font-mono text-[11px]"
        >
          {[1, 3, 7, 14, 30].map((d) => (
            <option key={d} value={d}>Last {d}d</option>
          ))}
        </select>

        <span className="ml-auto font-mono text-[11px] text-white/40">
          {rows.length} signals · {s?.total_resolved ?? 0} resolved · {s?.pending ?? 0} live
        </span>

        <button
          data-testid="reset-stats-btn"
          onClick={() => {
            if (
              confirm(
                "Clear the win/loss scoreboard? This deletes every resolved trade so stats start fresh from the recalibrated engine. Pending triggers are preserved."
              )
            ) {
              resetStats.mutate();
            }
          }}
          disabled={resetStats.isPending}
          className="px-2 py-1 border border-[#FF3B30]/40 hover:bg-[#FF3B30]/10 text-[#FF3B30] font-mono text-[10px] uppercase tracking-widest disabled:opacity-40"
          title="Clear resolved win/loss stats (does not affect pending triggers)"
        >
          {resetStats.isPending ? "Clearing…" : "Clear Stats"}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 p-3 border-b border-white/5" data-testid="engine-stats">
        <Metric label="Resolved" value={s?.total_resolved ?? 0} />
        <Metric
          label="Win Rate"
          value={`${s?.win_rate ?? 0}%`}
          color={(s?.win_rate ?? 0) >= 65 ? "#00FF66" : (s?.win_rate ?? 0) >= 50 ? "#F5C518" : "#FF3B30"}
        />
        <Metric label="Wins" value={s?.wins ?? 0} color="#00FF66" />
        <Metric label="TP1 Only" value={s?.tp1_only ?? 0} color="#F5C518" />
        <Metric label="Losses" value={s?.losses ?? 0} color="#FF3B30" />
        <Metric
          label="Avg R"
          value={s?.avg_r_multiple ?? 0}
          color={(s?.avg_r_multiple ?? 0) >= 0 ? "#00FF66" : "#FF3B30"}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 p-3 border-b border-white/5" data-testid="engine-adv-stats">
        <Metric
          label="Profit Factor"
          value={s?.profit_factor ?? 0}
          color={(s?.profit_factor ?? 0) >= 1.5 ? "#00FF66" : (s?.profit_factor ?? 0) >= 1 ? "#F5C518" : "#FF3B30"}
        />
        <Metric
          label="Expectancy R"
          value={s?.expectancy_r ?? 0}
          color={(s?.expectancy_r ?? 0) >= 0.3 ? "#00FF66" : (s?.expectancy_r ?? 0) >= 0 ? "#F5C518" : "#FF3B30"}
        />
        <Metric label="Avg Win R" value={s?.avg_win_r ?? 0} color="#00FF66" />
        <Metric label="Avg Loss R" value={s?.avg_loss_r ?? 0} color="#FF3B30" />
        <Metric
          label="Max DD"
          value={`${s?.max_drawdown_pct ?? 0}%`}
          color={(s?.max_drawdown_pct ?? 0) > -15 ? "#F5C518" : "#FF3B30"}
        />
        <Metric
          label="Sharpe"
          value={s?.sharpe ?? 0}
          color={(s?.sharpe ?? 0) >= 1 ? "#00FF66" : (s?.sharpe ?? 0) >= 0 ? "#F5C518" : "#FF3B30"}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-3 border-b border-white/5" data-testid="engine-lev-stats">
        <Metric
          label="Lev Max DD"
          value={`${s?.max_drawdown_leveraged_pct ?? 0}%`}
          color={(s?.max_drawdown_leveraged_pct ?? 0) > -30 ? "#F5C518" : "#FF3B30"}
        />
        <Metric label="Lev PnL" value={`${s?.total_leveraged_pnl_pct ?? 0}%`} color={(s?.total_leveraged_pnl_pct ?? 0) >= 0 ? "#00FF66" : "#FF3B30"} />
        <Metric label="Total PnL" value={`${s?.total_pnl_pct ?? 0}%`} color={(s?.total_pnl_pct ?? 0) >= 0 ? "#00FF66" : "#FF3B30"} />
        <Metric label="Pending" value={s?.pending ?? 0} color="#F5C518" />
      </div>

      <div className="flex-1 overflow-auto">
        <table className="w-full text-left" data-testid="history-table">
          <thead className="text-[10px] font-mono uppercase text-white/50 sticky top-0 bg-[#0f0f0f] border-b border-white/10">
            <tr>
              <th className="px-3 py-2 tracking-widest">Time</th>
              <th className="px-3 py-2 tracking-widest">Symbol</th>
              <th className="px-3 py-2 tracking-widest">Dir</th>
              <th className="px-3 py-2 tracking-widest">Mode</th>
              <th className="px-3 py-2 tracking-widest text-right">Score</th>
              <th className="px-3 py-2 tracking-widest text-right">Lev</th>
              <th className="px-3 py-2 tracking-widest text-right">Entry</th>
              <th className="px-3 py-2 tracking-widest text-right">Exit</th>
              <th className="px-3 py-2 tracking-widest text-right">R</th>
              <th className="px-3 py-2 tracking-widest text-right">Lev PnL</th>
              <th className="px-3 py-2 tracking-widest">Status</th>
            </tr>
          </thead>
          <tbody>
            {history.isLoading && (
              <tr>
                <td colSpan={11} className="p-6 text-center text-white/40 font-mono text-xs">Loading history<span className="terminal-cycle">...</span></td>
              </tr>
            )}
            {!history.isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={11} className="p-6 text-center text-white/40 font-mono text-xs">
                  No signals in this filter yet. The engine will record each triggered signal and track it through its horizon.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const isShort = r.direction === "short";
              const accent = isShort ? "#FF3B30" : "#00FF66";
              const resolved = r.status === "resolved";
              const rMult = resolved ? r.r_multiple : r.mfe_r;
              const rColor = (rMult ?? 0) >= 0 ? "#00FF66" : "#FF3B30";
              return (
                <tr
                  key={r.id}
                  onClick={() => onSelect?.(r.symbol)}
                  className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
                  data-testid={`history-row-${r.id}`}
                >
                  <td className="px-3 py-1.5 font-mono text-[11px] text-white/60">{timeAgo(r.created_at)}</td>
                  <td className="px-3 py-1.5 font-mono text-[12px] text-white font-semibold">{r.symbol}</td>
                  <td className="px-3 py-1.5 font-mono text-[10px]">
                    <span className="flex items-center gap-0.5 uppercase tracking-widest" style={{ color: accent }}>
                      {isShort ? <ArrowDown size={9} weight="bold" /> : <ArrowUp size={9} weight="bold" />}
                      {r.direction}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white/60">
                    {r.mode || "scalp"}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-[11px] text-right text-white">{r.score}</td>
                  <td className="px-3 py-1.5 font-mono text-[11px] text-right text-[#F5C518]">{r.recommended_leverage}×</td>
                  <td className="px-3 py-1.5 font-mono text-[11px] text-right text-white">{fmtNum(r.entry)}</td>
                  <td className="px-3 py-1.5 font-mono text-[11px] text-right text-white/70">
                    {resolved ? fmtNum(r.exit_price) : "—"}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-[11px] text-right" style={{ color: rColor }}>
                    {rMult !== undefined && rMult !== null ? `${rMult >= 0 ? "+" : ""}${rMult.toFixed(2)}R` : "—"}
                  </td>
                  <td
                    className="px-3 py-1.5 font-mono text-[11px] text-right"
                    style={{ color: resolved ? ((r.leveraged_pnl_pct ?? 0) >= 0 ? "#00FF66" : "#FF3B30") : "#666" }}
                  >
                    {resolved ? `${(r.leveraged_pnl_pct ?? 0) >= 0 ? "+" : ""}${(r.leveraged_pnl_pct ?? 0).toFixed(2)}%` : "—"}
                  </td>
                  <td className="px-3 py-1.5">
                    {resolved ? <OutcomePill outcome={r.outcome} /> : <StatusPill status="pending" mfeR={r.mfe_r} maeR={r.mae_r} />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Metric({ label, value, color = "#FFFFFF" }) {
  return (
    <div className="border border-white/10 bg-[#0f0f0f] px-3 py-2">
      <div className="text-white/40 font-mono text-[10px] uppercase tracking-widest">{label}</div>
      <div className="font-display text-base font-bold" style={{ color }}>{value}</div>
    </div>
  );
}
